/**
 * ตรรกะล้วนของหน้าจอกฎตรวจรับเข้า — แยกออกจาก JSX เพื่อให้ทดสอบกฎได้ตรง ๆ
 *
 * แนวเดียวกับ `lib/suppliers/suppliersView.ts`: สิ่งที่อยู่ที่นี่คือคำถามที่ตอบได้โดย
 * ไม่ต้องเรนเดอร์อะไรเลย — โดยเฉพาะ **การประกอบ body** ซึ่งเป็นด่านเดียวที่ทางเขียน
 * ทั้งสองเส้นของหน้าจอนี้เดินผ่าน
 *
 * สองข้อจำกัดที่ไฟล์นี้ถือไว้โดยเจตนา:
 *
 * 1. **ไม่มีฟังก์ชันที่แก้คู่ (product, supplier) ได้เลย** — `toUpdateQualityRuleBody`
 *    ไม่รับทั้งสองค่าเป็นพารามิเตอร์ด้วยซ้ำ คู่นี้คือสิ่งที่ unique constraint จับและ
 *    สิ่งที่การรับเข้าใช้ค้นหา (D-086)
 * 2. **ไม่มีค่าเริ่มต้นของธงการตรวจ** — `toCreateQualityRuleBody` คืน `null` ตราบที่
 *    ผู้ใช้ยังไม่ได้เลือก ระบบ default เป็น "ต้องตรวจ" อยู่แล้วเมื่อไม่มีกฎ กฎที่
 *    เขียนลงไปโดยไม่ได้ตัดสินใจจึงเป็นกฎที่ไม่ได้บอกอะไร และที่แย่กว่าคือคนเขียนอาจ
 *    เข้าใจว่าได้ยกเว้นการตรวจไปแล้ว
 */

import type {
  CreateQualityRuleBody,
  ProductSupplierQualityRule,
  UpdateQualityRuleBody,
} from './types';

/* --------------------------------------------------- ธงการตรวจบนหน้าจอ -- */

/**
 * ค่าของช่องเลือกธงการตรวจบนฟอร์ม
 *
 * เป็นสตริงสามค่า ไม่ใช่ `boolean` เพราะฟอร์มต้องแทน **"ยังไม่ได้เลือก"** ได้ ซึ่งเป็น
 * สถานะที่ `boolean` ไม่มีที่ให้ — และมันคือสถานะเริ่มต้นที่กล่องสร้างต้องอยู่
 */
export const INSPECTION_UNDECIDED = '';
export const INSPECTION_REQUIRED = 'REQUIRED';
export const INSPECTION_NOT_REQUIRED = 'NOT_REQUIRED';

export type InspectionChoice =
  | typeof INSPECTION_UNDECIDED
  | typeof INSPECTION_REQUIRED
  | typeof INSPECTION_NOT_REQUIRED;

export interface InspectionChoiceDescriptor {
  readonly value: typeof INSPECTION_REQUIRED | typeof INSPECTION_NOT_REQUIRED;
  readonly label: string;
  readonly summary: string;
}

/**
 * สองตัวเลือกที่ฟอร์มเสนอ — **ไม่มีตัวที่สามที่แปลว่า "ตามค่าเริ่มต้น"**
 *
 * ข้อความของแต่ละตัวบอกว่า **เกิดอะไรขึ้นกับของจริงตอนมันมาถึงท่ารับ** ไม่ใช่แค่
 * ทวนชื่อฟิลด์: คนที่ตั้งกฎนี้คือคนที่ตัดสินว่าของล็อตหน้าจะถูกกักไว้หรือเดินเข้าคลัง
 * ได้เลย ซึ่งเป็นการตัดสินใจที่มีผลต่อสายการผลิตโดยตรง
 */
export const INSPECTION_CHOICE_DESCRIPTORS: readonly InspectionChoiceDescriptor[] = [
  {
    value: INSPECTION_REQUIRED,
    label: 'ต้องตรวจก่อนใช้',
    summary:
      'ของที่รับเข้าจากคู่นี้ถูกกักไว้รอผลตรวจ เบิกไปใช้ในการผลิตไม่ได้จนกว่าจะมีผู้ตัดสินคุณภาพ',
  },
  {
    value: INSPECTION_NOT_REQUIRED,
    label: 'ไม่ต้องตรวจ',
    summary:
      'ของที่รับเข้าจากคู่นี้พร้อมใช้ทันทีโดยไม่ต้องรอผลตรวจ — เป็นการยกเว้นการตรวจที่มีผู้ตัดสินและถูกบันทึกไว้',
  },
];

/**
 * ชื่อที่อ่านออกของธงการตรวจ — **ไม่ใช่ `true`/`false` ดิบ ๆ บนตาราง**
 *
 * คนที่อ่านตารางนี้คือเจ้าหน้าที่คลังและฝ่ายคุณภาพ ไม่ใช่คนเขียนโปรแกรม คอลัมน์ที่
 * เขียนว่า `false` ไม่ได้บอกเขาว่าเกิดอะไรขึ้นกับของ
 */
export function incomingInspectionLabel(required: boolean): string {
  return required ? 'ต้องตรวจก่อนใช้' : 'ไม่ต้องตรวจ';
}

/** สีของป้าย — "ต้องตรวจ" คือของที่ยังเบิกไม่ได้ จึงเป็นโทนเตือน ไม่ใช่โทนสำเร็จ */
export type InspectionTone = 'warning' | 'success';

export function incomingInspectionTone(required: boolean): InspectionTone {
  return required ? 'warning' : 'success';
}

/** แปลงค่าที่ฟอร์มถืออยู่เป็นค่าที่ส่งได้ — `null` = ยังไม่ได้เลือก */
export function inspectionChoiceToFlag(choice: string): boolean | null {
  switch (choice) {
    case INSPECTION_REQUIRED:
      return true;
    case INSPECTION_NOT_REQUIRED:
      return false;
    default:
      return null;
  }
}

/** แปลงค่าที่เซิร์ฟเวอร์ส่งมาเป็นค่าเริ่มต้นของช่องเลือกในกล่องแก้ไข */
export function flagToInspectionChoice(required: boolean): InspectionChoice {
  return required ? INSPECTION_REQUIRED : INSPECTION_NOT_REQUIRED;
}

/* -------------------------------------------------------------- ค้นหา --- */

/**
 * กรองรายการกฎด้วยคำค้นเดียว — เทียบทั้งรหัสสินค้าและรหัสผู้ขาย
 *
 * ทั้งสองคอลัมน์เพราะคำถามจริงมาได้จากสองทาง: "สินค้าตัวนี้มีกฎกับผู้ขายรายไหนบ้าง"
 * และ "ผู้ขายรายนี้มีกฎกับสินค้าตัวไหนบ้าง" — คู่หนึ่งกฎอยู่ที่จุดตัดของทั้งสอง
 *
 * กรอง **ฝั่งเบราว์เซอร์จากหน้าที่โหลดมาแล้ว** ไม่ยิงคำขอใหม่: ตัวกรองของ contract
 * (`product_code`/`supplier_code`) เทียบแบบ **ตรงทั้งค่า** ส่วนช่องนี้เป็นการค้นหา
 * บางส่วน ซึ่งเป็นคนละอย่างกัน — การยิงตัวกรองไปด้วยคำที่พิมพ์ค้างครึ่งทางจะได้
 * หน้าว่างกลับมาทุกครั้งจนกว่าจะพิมพ์รหัสครบพอดี
 */
export function filterQualityRules(
  items: readonly ProductSupplierQualityRule[],
  query: string,
): readonly ProductSupplierQualityRule[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return items;
  }
  return items.filter(
    (rule) =>
      rule.product_code.toLocaleLowerCase().includes(needle) ||
      rule.supplier_code.toLocaleLowerCase().includes(needle),
  );
}

/* ------------------------------------------------------- ประกอบคำขอเขียน -- */

/**
 * ประกอบ body ของ `POST` — คืน `null` เมื่อยังประกอบไม่ได้
 *
 * คืน `null` ในสามกรณี และข้อที่สามคือข้อสำคัญ:
 *  1. รหัสสินค้าว่าง
 *  2. รหัสผู้ขายว่าง
 *  3. **ยังไม่ได้เลือกว่าต้องตรวจหรือไม่** — ธงนี้ไม่มี default บนสายโดยตั้งใจของ
 *     contract (D-086) ปุ่มบันทึกที่กดได้ทั้งที่ยังไม่ได้เลือก คือปุ่มที่ยอมให้คน
 *     เขียนกฎโดยไม่ได้ตัดสินใจ
 *
 * ทั้งสองรหัสถูก `trim()` เพราะช่องว่างหน้า/หลังเป็นสิ่งที่มองไม่เห็นบนหน้าจอ แต่ทำให้
 * รหัสไม่ตรงกับรหัสจริง แล้วคำขอจะจบที่ `422 PRODUCT_NOT_FOUND` ทั้งที่ผู้ใช้คัดลอก
 * รหัสที่ถูกต้องมา
 *
 * `note` ที่ว่างเปล่ากลายเป็น **คีย์ที่หายไปทั้งคีย์** ไม่ใช่สตริงว่างที่ถูกส่งออกไป
 */
export function toCreateQualityRuleBody(
  productCode: string,
  supplierCode: string,
  inspectionChoice: string,
  note: string,
): CreateQualityRuleBody | null {
  const product = productCode.trim();
  const supplier = supplierCode.trim();
  const flag = inspectionChoiceToFlag(inspectionChoice);
  if (product === '' || supplier === '' || flag === null) {
    return null;
  }

  const trimmedNote = note.trim();
  return {
    product_code: product,
    supplier_code: supplier,
    incoming_inspection_required: flag,
    ...(trimmedNote === '' ? {} : { note: trimmedNote }),
  };
}

/**
 * ประกอบ body ของ `PATCH` — **เฉพาะสิ่งที่เปลี่ยนจริง**
 *
 * คืน `null` ในสองกรณีที่ต่างกันแต่ให้ผลเดียวกันคือ "ยังไม่มีคำขอให้ส่ง":
 *  1. ธงการตรวจอ่านไม่ออก (ช่องเลือกถูกล้างค่า) — คอลัมน์นี้ `NOT NULL` และเป็น
 *     คำตอบทั้งหมดของกฎข้อนี้
 *  2. ไม่มีอะไรเปลี่ยนจากค่าที่โหลดมา — เซิร์ฟเวอร์ยินดีรับและจะเขียนแถว `audit_log`
 *     ให้ แต่คำขอที่ไม่ได้ขออะไรเลยคือแถว audit ที่บันทึกว่าไม่มีการเปลี่ยนแปลง ซึ่ง
 *     ทำให้สมุดที่ผู้ตรวจสอบใช้อ่านยากขึ้นโดยไม่ได้อะไรกลับมา
 *
 * หมายเหตุที่ถูกล้างจนว่างเปล่าเดินทางไปเป็น **`note: null`** ไม่ใช่สตริงว่าง: contract
 * บอกว่า `null` คือคำสั่งล้างค่า ส่วนสตริงว่างคือหมายเหตุที่ว่างเปล่า ซึ่งจะทำให้
 * รายการมี "หมายเหตุ" ที่ไม่มีข้อความอยู่ข้างใน
 *
 * `product_code`/`supplier_code` ไม่มีทางเข้ามาถึงที่นี่ได้เลย — ทั้งสองไม่ใช่
 * พารามิเตอร์ของฟังก์ชันนี้ และไม่ใช่คีย์ของ `UpdateQualityRuleBody`
 */
export function toUpdateQualityRuleBody(
  current: ProductSupplierQualityRule,
  inspectionChoice: string,
  note: string,
): UpdateQualityRuleBody | null {
  const flag = inspectionChoiceToFlag(inspectionChoice);
  if (flag === null) {
    return null;
  }

  const trimmedNote = note.trim();
  const nextNote = trimmedNote === '' ? null : trimmedNote;

  const body: UpdateQualityRuleBody = {
    ...(flag === current.incoming_inspection_required
      ? {}
      : { incoming_inspection_required: flag }),
    ...(nextNote === current.note ? {} : { note: nextNote }),
  };

  return body.incoming_inspection_required === undefined && body.note === undefined
    ? null
    : body;
}
